package com.rosario.juridico.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.CardGiftcard
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Payment
import androidx.compose.material.icons.filled.Shield
import androidx.compose.material.icons.filled.ShoppingCart
import androidx.compose.material.icons.filled.Star
import androidx.compose.material3.*
import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.getPlatform
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.premium.PremiumManager
import com.rosario.juridico.premium.PremiumPlan
import com.rosario.juridico.premium.PremiumPurchaseManager
import com.rosario.juridico.premium.PurchaseSource
import com.rosario.juridico.premium.PurchaseState
import com.rosario.juridico.ui.screens.toPesos

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PantallaPaywall(navController: NavController) {
    val purchaseState = PremiumPurchaseManager.purchaseState
    val isLoading = purchaseState is PurchaseState.Processing
    val error = (purchaseState as? PurchaseState.Error)?.message

    val billingProvider = com.rosario.juridico.marketplace.billing.getBillingProvider()
    val realPrice by (billingProvider?.premiumPrice ?: MutableStateFlow(null)).collectAsState()

    val platform = remember { getPlatform().name }
    val source = remember(platform) {
        if (platform.contains("Android", true)) PurchaseSource.ANDROID_PLAY_STORE
        else PurchaseSource.WEB_MERCADOPAGO
    }

    var showPromoDialog by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        // Asegurar que la pantalla inicie en estado limpio
        PremiumPurchaseManager.resetState()
    }

    LaunchedEffect(purchaseState) {
        if (purchaseState is PurchaseState.Success) {
            navController.popBackStack()
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Membresía Premium") },
                navigationIcon = {
                    IconButton(onClick = { navController.popBackStack() }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Volver")
                    }
                }
            )
        }
    ) { paddingValues ->
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Icon(
                    Icons.Default.Star,
                    contentDescription = null,
                    modifier = Modifier.size(80.dp),
                    tint = MaterialTheme.colorScheme.primary
                )
                
                Spacer(Modifier.height(16.dp))
                
                Text(
                    "Desbloqueá todo el potencial",
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )
                
                Spacer(Modifier.height(8.dp))
                
                Text(
                    "Premium Multidispositivo: Usalo en tu celular y en tu computadora (Web)",
                    style = MaterialTheme.typography.bodyLarge,
                    textAlign = TextAlign.Center,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary
                )

                Spacer(Modifier.height(8.dp))

                Text(
                    "Toda tu información sincronizada en tiempo real.",
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )

                Spacer(Modifier.height(16.dp))
                
                BenefitItem("Uso ilimitado de las 9 calculadoras legales")
                BenefitItem("Generador de contratos con IA y Agenda Sync")
                BenefitItem("Acceso Web Completo (juridicorosario.com)")
                BenefitItem("Consultas legales ilimitadas")
                BenefitItem("Gestión avanzada de expedientes")
                
                Spacer(Modifier.height(32.dp))

                val isPremium = PremiumManager.isPremium
                val purchaseState = PremiumPurchaseManager.purchaseState
                val isProcessing = purchaseState is PurchaseState.Processing

                if (isPremium) {
                    // Estado de Suscripción Activa
                    Card(
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f)),
                        shape = RoundedCornerShape(16.dp),
                        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp)
                    ) {
                        Column(
                            modifier = Modifier.padding(24.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Icon(
                                Icons.Default.CheckCircle,
                                contentDescription = null,
                                tint = Color(0xFF4CAF50),
                                modifier = Modifier.size(48.dp)
                            )
                            Spacer(Modifier.height(16.dp))
                            Text(
                                "Suscripción Activa",
                                style = MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onPrimaryContainer
                            )
                            Spacer(Modifier.height(8.dp))
                            Text(
                                "Ya eres usuario Premium. Tienes acceso ilimitado a todas las funciones.",
                                textAlign = TextAlign.Center,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.8f)
                            )
                            
                            val vencimiento = PremiumManager.vencimiento
                            if (vencimiento > 0) {
                                Spacer(Modifier.height(16.dp))
                                val fechaVencimiento = rememberFormattedDate(vencimiento)
                                Text(
                                    "Vence el: $fechaVencimiento",
                                    style = MaterialTheme.typography.labelLarge,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            }
                        }
                    }
                } else if (isProcessing) {
                    // Estado de Procesamiento (Esperando Webhook/Sync)
                    Card(
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                        shape = RoundedCornerShape(16.dp),
                        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp)
                    ) {
                        Column(
                            modifier = Modifier.padding(24.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            CircularProgressIndicator(modifier = Modifier.size(48.dp))
                            Spacer(Modifier.height(24.dp))
                            Text(
                                "Procesando tu Suscripción",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold
                            )
                            Spacer(Modifier.height(8.dp))
                            Text(
                                "Estamos confirmando tu pago con Mercado Pago. Esto puede demorar unos segundos...",
                                textAlign = TextAlign.Center,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                } else {
                    if (error != null) {
                        Text(
                            text = error,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(bottom = 16.dp)
                        )
                    }

                    val isAndroid = platform.contains("Android", true)

                    PlanCard(
                        title = if (isAndroid) "Suscripción Premium" else "Plan Mensual",
                        price = if (isAndroid) (realPrice ?: "Consultando...") else PremiumPlan.MENSUAL.precio.toPesos(),
                        subtitle = if (isAndroid) "Facturado por Google Play" else "Suscripción recurrente",
                        source = source,
                        onClick = {
                            PremiumPurchaseManager.startPremiumPurchase(
                                PremiumPlan.MENSUAL,
                                source
                            )
                        },
                        enabled = !isLoading && (realPrice != null || !isAndroid)
                    )
                    
                    if (!isAndroid) {
                        Spacer(Modifier.height(16.dp))
                        
                        PlanCard(
                            title = "Plan Anual",
                            price = PremiumPlan.ANUAL.precio.toPesos(),
                            subtitle = "Pago único (Ahorrás 25%)",
                            isRecommended = true,
                            source = source,
                            onClick = {
                                PremiumPurchaseManager.startPremiumPurchase(
                                    PremiumPlan.ANUAL,
                                    source
                                )
                            },
                            enabled = !isLoading
                        )
                    }

                    // ──────── Acceso Promocional / Código Institucional ────────
                    Spacer(Modifier.height(20.dp))
                    
                    TextButton(
                        onClick = { showPromoDialog = true },
                        modifier = Modifier.padding(vertical = 4.dp),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.CardGiftcard,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = MaterialTheme.colorScheme.secondary
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = "¿Tienes un código institucional?",
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.SemiBold,
                            color = MaterialTheme.colorScheme.secondary
                        )
                    }
                }
                
                if (isLoading) {
                    Spacer(Modifier.height(24.dp))
                    CircularProgressIndicator(strokeWidth = 3.dp)
                    Text(
                        text = if (source == PurchaseSource.WEB_MERCADOPAGO) "Conectando con Mercado Pago..." else "Conectando con Google Play...",
                        modifier = Modifier.padding(top = 12.dp),
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.primary
                    )
                }
                
                Spacer(Modifier.height(40.dp))

                // Footer de Seguridad
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color.Gray.copy(alpha = 0.05f)),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                Icons.Default.Shield, 
                                null, 
                                tint = Color(0xFF4CAF50), 
                                modifier = Modifier.size(16.dp)
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                "Pago 100% Seguro",
                                style = MaterialTheme.typography.labelLarge,
                                fontWeight = FontWeight.Bold
                            )
                        }
                        Spacer(Modifier.height(8.dp))
                        Text(
                            text = if (source == PurchaseSource.WEB_MERCADOPAGO) 
                                "Tu transacción se procesa de forma segura a través de los servidores oficiales de Mercado Pago. Jurídico Rosario no almacena los datos de tu tarjeta."
                                else "Tu suscripción es gestionada de forma segura por Google Play Store.",
                            style = MaterialTheme.typography.labelSmall,
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            lineHeight = 16.sp
                        )
                        
                        if (source == PurchaseSource.WEB_MERCADOPAGO) {
                            Spacer(Modifier.height(12.dp))
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Text("Aceptamos:", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                                Spacer(Modifier.width(8.dp))
                                Text("Tarjetas, Débito y Efectivo", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                            }
                        }
                    }
                }
            }

            // ──────── Dialog Promocional con Desmontaje Secuencial Seguro ────────
            if (showPromoDialog) {
                PromoCodeDialog(
                    onDismissRequest = { showPromoDialog = false },
                    onSuccess = { _ ->
                        // Patrón de Desmontaje Secuencial Seguro para Skiko / Compose Multiplatform:
                        // 1. Cerrar primero el overlay del diálogo
                        showPromoDialog = false
                        // 2. Ejecutar la actualización de estado y navegación en corrutina con retardo de estabilización
                        scope.launch {
                            val uid = FirebaseProxy.getCurrentUserId()
                            if (uid != null) {
                                PremiumManager.loadUserProfile(uid, forceRefresh = true)
                            }
                            delay(150) // Estabilización UI para evitar render exception en Skiko
                            navController.popBackStack()
                        }
                    }
                )
            }
        }
    }
}

@Composable
fun BenefitItem(text: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            Icons.Default.CheckCircle,
            contentDescription = null,
            tint = Color(0xFF4CAF50),
            modifier = Modifier.size(20.dp)
        )
        Spacer(Modifier.width(12.dp))
        Text(text, style = MaterialTheme.typography.bodyMedium)
    }
}

@Composable
fun PlanCard(
    title: String,
    price: String,
    subtitle: String,
    source: PurchaseSource,
    isRecommended: Boolean = false,
    onClick: () -> Unit,
    enabled: Boolean = true
) {
    val borderColor = if (isRecommended) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
    val borderWidth = if (isRecommended) 2.dp else 1.dp

    Card(
        onClick = onClick,
        enabled = enabled,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        border = androidx.compose.foundation.BorderStroke(borderWidth, borderColor),
        colors = CardDefaults.cardColors(
            containerColor = if (isRecommended) MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f) else MaterialTheme.colorScheme.surface
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (isRecommended) 4.dp else 0.dp)
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {
            if (isRecommended) {
                Surface(
                    color = MaterialTheme.colorScheme.primary,
                    shape = RoundedCornerShape(bottomStart = 12.dp),
                    modifier = Modifier.align(Alignment.TopEnd)
                ) {
                    Text(
                        "MÁS POPULAR",
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                        color = Color.White,
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.ExtraBold
                    )
                }
            }
            
            Column(modifier = Modifier.padding(24.dp)) {
                Text(
                    text = title, 
                    style = MaterialTheme.typography.titleMedium, 
                    fontWeight = FontWeight.Bold,
                    color = if (isRecommended) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface
                )
                Spacer(Modifier.height(8.dp))
                Row(verticalAlignment = Alignment.Bottom) {
                    Text(price, style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.ExtraBold)
                    Spacer(Modifier.width(8.dp))
                    Text(subtitle, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(bottom = 4.dp))
                }
                
                Spacer(Modifier.height(16.dp))
                
                // Botón de acción con Branding específico
                Button(
                    onClick = onClick,
                    enabled = enabled,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = if (source == PurchaseSource.WEB_MERCADOPAGO) {
                        ButtonDefaults.buttonColors(
                            containerColor = Color(0xFF009EE3), // Azul MP
                            contentColor = Color.White
                        )
                    } else {
                        ButtonDefaults.buttonColors()
                    }
                ) {
                    if (source == PurchaseSource.WEB_MERCADOPAGO) {
                        Icon(Icons.Default.Payment, null, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text("Pagar con Mercado Pago", fontWeight = FontWeight.Bold)
                    } else {
                        Icon(Icons.Default.ShoppingCart, null, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text("Suscribirse con Google Play", fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}
